import React, { useState } from "react";
import { Alert, Button, ButtonGroup, Modal } from "../../components/ui";
import { useRequestContext } from "../hooks/useRequest";

/**
 * "Remove from this deal" for one slot of a combined deal.
 *
 * Detach, not delete: the request leaves the deal and carries on as a
 * standalone request. That is not obvious from a button labelled "remove", and
 * the rep is the one who has to explain it to the client, so the dialog says it
 * in words and shows the backend's own dry-run plan — what happens to the
 * product's line on the order, whether the deal stops being a part-exchange —
 * rather than prose written here that could drift from what the endpoint does.
 *
 * It also owns the OUTCOME, the same way SwitchXeroOrgModal does: the request
 * runs with the dialog open and a refusal is rendered inline. The refusals are
 * actionable ("remove the deposit first", "void the invoice first") and are the
 * whole point of the pre-flight — a detached toast is easy to miss.
 *
 * Not offered on the deal's FIRST product: slot 0 lives in the singular
 * product/request/mainData state and removing it would need a sibling promoted
 * into it, which no code does. The backend refuses it too.
 */

/** The reasons a 422 carries, whatever shape the validation error arrived in. */
function refusalReasons(err) {
  const errors = err?.response?.data?.errors;
  if (errors) {
    return Object.values(errors).flat().filter(Boolean);
  }
  const message = err?.response?.data?.message;
  return message ? [message] : ["Could not remove this request from the deal."];
}

/** Plain-language consequences, read off the plan the backend generated. */
function consequences(plan) {
  const out = [
    "It becomes a separate request — nothing is deleted, and you can open it on its own afterwards.",
  ];
  if (plan?.order_item) {
    out.push("Its product is taken off this deal's order and the order total drops accordingly.");
  }
  if (plan?.dissolves_px) {
    out.push("This is the last trade-in, so the deal stops being a part-exchange.");
  }
  (plan?.warnings ?? []).forEach((warning) => {
    if (!out.includes(warning)) out.push(warning);
  });
  return out;
}

export function DetachRequestControl({ pxSellIdx = null, mainSlotIdx = null, label }) {
  const { detachSlotPlan, detachSlot } = useRequestContext();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [errors, setErrors] = useState([]);

  // The synthetic step the hook resolves the slot's own request from — the same
  // shape getContext already honours (pxSellIdx / mainSlotIdx).
  const step = { pxSellIdx, mainSlotIdx };

  const onOpen = async () => {
    setOpen(true);
    setPlan(null);
    setErrors([]);
    setLoading(true);
    try {
      setPlan(await detachSlotPlan(step));
    } catch (err) {
      setErrors(refusalReasons(err));
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    setSubmitting(true);
    setErrors([]);
    try {
      await detachSlot(step);
      setOpen(false);
    } catch (err) {
      setErrors(refusalReasons(err));
    } finally {
      setSubmitting(false);
    }
  };

  const blockers = plan?.blockers ?? [];
  const blocked = loading || submitting || blockers.length > 0 || !plan;

  const close = () => setOpen(false);

  return (
    <>
      <ButtonGroup align="end" className="rc-request-v2__detach">
        <Button variant="danger" size="sm" data-btn="request-detach-open" onClick={onOpen}>
          Remove from this deal
        </Button>
      </ButtonGroup>

      <Modal
        open={open}
        onClose={close}
        dismissible={!submitting}
        danger
        title={`Remove ${label || "this product"} from the deal?`}
        footer={
          <ButtonGroup align="end">
            <Button variant="ghost" data-btn="request-detach-cancel" onClick={close} disabled={submitting}>
              {blockers.length > 0 ? "Close" : "Cancel"}
            </Button>
            {blockers.length === 0 && (
              <Button variant="danger" data-btn="request-detach-confirm" onClick={onConfirm} disabled={blocked}>
                {submitting ? "Removing…" : "Remove & keep as separate request"}
              </Button>
            )}
          </ButtonGroup>
        }
      >
        {loading && <p>Checking what this would do…</p>}

        {!loading && blockers.length > 0 && (
          <>
            <p>This cannot be removed yet:</p>
            {blockers.map((blocker) => (
              <Alert key={blocker} tone="error">
                {blocker}
              </Alert>
            ))}
          </>
        )}

        {!loading && plan && blockers.length === 0 && (
          <>
            <p>Here is what will happen:</p>
            <ul>
              {consequences(plan).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {plan.dissolves_px && (
              <Alert tone="warning">Removing the last trade-in turns this back into a plain order.</Alert>
            )}
          </>
        )}

        {errors.length > 0 &&
          errors.map((error) => (
            <Alert key={error} tone="error">
              {error}
            </Alert>
          ))}
      </Modal>
    </>
  );
}

export default DetachRequestControl;
